/**
 * Warstwa OpenLayers rysujaca wypieczone instrukcje z QGIS-a.
 *
 * Dlaczego wlasny canvas, a nie ol/style: symbolika mapy zasadniczej operuje
 * szerokosciami i wzorami kresek w METRACH TERENOWYCH, obroconymi symbolami SVG
 * i markerami na wierzcholkach. Mapowanie tego na ol.Style konczy sie
 * przyblizeniami; tutaj rysujemy dokladnie to, co policzyl QGIS, a od
 * OpenLayers bierzemy tylko widok, projekcje i interakcje.
 */
import Layer from 'ol/layer/Layer'
import type { FrameState } from 'ol/Map'
import type { Op, RenderLayer, RenderFeature, Label } from './api'

export interface OpsData {
  layers: RenderLayer[]
  labels: Label[]
  symbols: Record<string, string>
}

const MARKER_MIN_PX = 2
const LABEL_MIN_PX = 5

/**
 * Podswietlenie zaznaczonego obiektu - poswiata pod jego wlasna symbolika,
 * tak jak robi to QGIS, zamiast prostokata zasiegu.
 *
 * Ani zolty, ani czerwony (domyslne barwy zaznaczenia i identyfikacji w
 * QGIS-ie) nie nadaja sie na mape zasadnicza: oba siedza w barwach branz
 * GESUT - gaz jest zolty, elektroenergetyka czerwona - wiec zaznaczenie
 * zlewaloby sie z trescia mapy. Magenta w tej symbolice nie wystepuje.
 */
const SEL_FILL = 'rgba(224,24,155,.20)'
const SEL_HALO = 'rgba(224,24,155,.55)'
const SEL_PAD_PX = 5

/**
 * Zapowiedz trafienia - obiekt wskazany na liscie trafien, ale jeszcze nie
 * wybrany. Sam kontur, slabszy i cienszy niz zaznaczenie, zeby przy dwoch
 * podswietleniach naraz bylo jasne, ktore z nich jest tym wlasciwym.
 */
const PRE_HALO = 'rgba(224,24,155,.32)'
const PRE_PAD_PX = 3

/** Obiekt trafiony klknieciem; dim/metric ustawiaja kolejnosc w stosie. */
export interface PickHit { layer: string; id: number; dim: number; metric: number }

export class OpsLayer extends Layer<any, any> {
  private canvas = document.createElement('canvas')
  private ctx = this.canvas.getContext('2d')!
  private images = new Map<string, HTMLImageElement>()

  data: OpsData = { layers: [], labels: [], symbols: {} }
  visible_ = new Set<string>()
  showLabels = true

  /**
   * Bialy papier pod rysunkiem mapy zasadniczej.
   *
   * Gasnie, gdy pod mapa lezy podklad - inaczej plotno tej warstwy przykrywa
   * go w calosci i podklad, choc poprawnie wczytany, jest niewidoczny.
   */
  showPaper = true
  selected: number | null = null
  /** Obiekt wskazany na liscie trafien - podswietlany slabiej niz zaznaczony. */
  hover: number | null = null
  onRedraw?: () => void

  constructor() {
    super({})
    this.canvas.style.position = 'absolute'
    this.canvas.style.top = '0'
    this.canvas.style.left = '0'
  }

  setData(d: OpsData, visible: Set<string>) {
    this.data = d
    this.visible_ = visible
    // bbox per obiekt - potrzebny do trafiania kliknieciem bez odpytywania serwera
    for (const L of d.layers) {
      for (const f of L.features) if (!f.bb) f.bb = bboxOf(f.ops)
    }
    this.changed()
  }

  render(frameState: FrameState): HTMLElement {
    const { size, viewState, pixelRatio } = frameState
    const [w, h] = size
    const { center, resolution } = viewState

    const cw = Math.round(w * pixelRatio)
    const ch = Math.round(h * pixelRatio)
    if (this.canvas.width !== cw || this.canvas.height !== ch) {
      this.canvas.width = cw
      this.canvas.height = ch
      this.canvas.style.width = w + 'px'
      this.canvas.style.height = h + 'px'
    }

    const ctx = this.ctx
    ctx.setTransform(pixelRatio, 0, 0, pixelRatio, 0, 0)
    ctx.clearRect(0, 0, w, h)

    // Papier mapy zasadniczej jest bialy z przepisow - kolory symboliki sa
    // dobrane pod bialy podklad, nie pod motyw przegladarki. Przy wlaczonym
    // podkladzie plotno zostaje przezroczyste, zeby bylo go widac.
    if (this.showPaper) {
      ctx.fillStyle = '#ffffff'
      ctx.fillRect(0, 0, w, h)
    }

    const k = 1 / resolution
    const sx = (x: number) => (x - center[0]) * k + w / 2
    const sy = (y: number) => h / 2 - (y - center[1]) * k

    ctx.lineCap = 'butt'
    ctx.lineJoin = 'round'

    for (const L of this.data.layers) {
      if (!this.visible_.has(L.name)) continue
      for (const f of L.features) {
        for (const o of f.ops) this.drawOp(ctx, o, sx, sy, k)
      }
    }

    if (this.showLabels) this.drawLabels(ctx, sx, sy, k)
    // Zapowiedz idzie pod zaznaczeniem: gdy oba wskazuja ten sam obiekt,
    // widac tylko mocniejsze z podswietlen.
    if (this.hover !== null && this.hover !== this.selected) {
      this.drawHighlight(this.hover, false, ctx, sx, sy, k)
    }
    if (this.selected !== null) this.drawHighlight(this.selected, true, ctx, sx, sy, k)

    this.onRedraw?.()
    return this.canvas
  }

  // ------------------------------------------------------------ instrukcje

  private drawOp(ctx: CanvasRenderingContext2D, o: Op,
                 sx: (x: number) => number, sy: (y: number) => number, k: number) {
    if (o.op === 'fill') {
      ctx.beginPath()
      for (const poly of o.g) {
        for (const ring of poly) {
          ring.forEach((p, i) => i ? ctx.lineTo(sx(p[0]), sy(p[1])) : ctx.moveTo(sx(p[0]), sy(p[1])))
          ctx.closePath()
        }
      }
      if (o.f) { ctx.fillStyle = o.f; ctx.fill('evenodd') }
      if (o.c) {
        ctx.strokeStyle = o.c
        ctx.lineWidth = Math.max(o.w * k, 0.6)
        ctx.setLineDash([])
        ctx.stroke()
      }
      return
    }

    if (o.op === 'line') {
      ctx.beginPath()
      for (const ln of asLines(o.g)) {
        ln.forEach((p, i) => i ? ctx.lineTo(sx(p[0]), sy(p[1])) : ctx.moveTo(sx(p[0]), sy(p[1])))
      }
      ctx.strokeStyle = o.c
      ctx.lineWidth = Math.max(o.w * k, 0.55)
      ctx.setLineDash(o.d ? o.d.map(v => Math.max(v * k, 1)) : [])
      ctx.stroke()
      ctx.setLineDash([])
      return
    }

    if (o.op === 'marker') {
      const s = Math.max(o.s * k, MARKER_MIN_PX)
      for (const p of o.g) {
        ctx.save()
        ctx.translate(sx(p[0]), sy(p[1]))
        // Kat jest ZGODNY z ruchem wskazowek zegara (jak QPainter::rotate w
        // QGIS), a canvas z osia Y w dol obraca tak samo - wiec znak dodatni.
        // Ta sama konwencja obowiazuje etykiety.
        if (o.rot) ctx.rotate(o.rot * Math.PI / 180)
        markerPath(ctx, o.shape, s)
        const strokeOnly = STROKE_ONLY.has(o.shape)
        // Ksztalty otwarte (kreska, krzyz, grot strzalki) sa w QGIS rysowane
        // wylacznie obrysem - wypelnienie domknelo by je w figure.
        if (o.f && !strokeOnly) { ctx.fillStyle = o.f; ctx.fill() }
        if (o.w > 0 || strokeOnly) {
          ctx.strokeStyle = o.c
          ctx.lineWidth = Math.max(o.w * k, 0.55)
          ctx.stroke()
        }
        ctx.restore()
      }
      return
    }

    // svg - 'u' to klucz w slowniku symbolow, nie sam obrazek
    const src = this.data.symbols[o.u]
    if (!src) return
    const im = this.image(src)
    if (!im.complete || !im.naturalWidth) return
    // Rozmiar symbolu to jego SZEROKOSC, nie bok kwadratu: QGIS skaluje
    // wysokosc proporcjonalnie do viewBox (svgAsImage(size=100) dla hydrantu
    // o viewBox 0.002 x 0.003 zwraca obraz 100x147 px). Rysowany w kwadracie
    // hydrant wychodzil splaszczony, a jego okrag - elipsa.
    const w = Math.max(o.s * k, 3)
    const h = w * im.naturalHeight / im.naturalWidth
    for (const p of o.g) {
      ctx.save()
      ctx.translate(sx(p[0]), sy(p[1]))
      if (o.rot) ctx.rotate(o.rot * Math.PI / 180)
      ctx.drawImage(im, -w / 2, -h / 2, w, h)
      ctx.restore()
    }
  }

  /** Proporcja symbolu (wysokosc/szerokosc); 1, dopoki obrazek sie nie wczyta. */
  private aspect(u: string): number {
    const src = this.data.symbols[u]
    if (!src) return 1
    const im = this.images.get(src)
    return im?.naturalWidth ? im.naturalHeight / im.naturalWidth : 1
  }

  private image(u: string): HTMLImageElement {
    let im = this.images.get(u)
    if (!im) {
      im = new Image()
      im.onload = () => this.changed()
      im.src = u
      this.images.set(u, im)
    }
    return im
  }

  // ------------------------------------------------------------- etykiety

  private drawLabels(ctx: CanvasRenderingContext2D,
                     sx: (x: number) => number, sy: (y: number) => number, k: number) {
    ctx.textBaseline = 'alphabetic'
    for (const l of this.data.labels) {
      if (!this.visible_.has(l.l)) continue
      const px = l.fs * k
      if (px < LABEL_MIN_PX) continue          // nieczytelne - nie zasmiecamy

      const lines = String(l.t).split(/\r?\n/)
      ctx.font = `${px.toFixed(2)}px Arial, Helvetica, sans-serif`

      const wMax = Math.max(...lines.map(s => ctx.measureText(s).width))
      const lead = px * 1.15
      const hAll = lead * lines.length

      // Cwiartka liczona wzgledem PUNKTU WSTAWIENIA (tak jak w QGIS), nie
      // wzgledem srodka napisu: 0..2 gora, 3..5 srodek, 6..8 dol;
      // 0/3/6 w lewo, 1/4/7 na srodek, 2/5/8 w prawo.
      const q = l.q ?? 4
      const dx = [-wMax, -wMax / 2, 0][q % 3]
      const dy = [0, hAll / 2, hAll][Math.floor(q / 3)]

      ctx.save()
      ctx.translate(sx(l.x), sy(l.y))
      // Ten sam znak co dla markerow: QGIS obraca etykiete ZGODNIE z ruchem
      // wskazowek (sprawdzone w app.diag_labelrot - napis obrocony o +45 idzie
      // z lewego-gornego do prawego-dolnego). Ujemny znak obracal je lustrzanie,
      // co bylo niewidoczne dla napisow poziomych i rzucalo sie w oczy dla reszty.
      if (l.rot) ctx.rotate(l.rot * Math.PI / 180)
      ctx.textAlign = 'left'

      // Otoczke rysujemy tylko gdy styl jej zada; wczesniej byla dokladana
      // zawsze, co pogrubialo kazdy napis.
      if (l.hc) {
        ctx.lineWidth = Math.max(l.halo * k * 2, 1)
        ctx.strokeStyle = l.hc
        ctx.lineJoin = 'round'
        lines.forEach((s, i) => ctx.strokeText(s, dx, dy - hAll + lead * (i + 0.8)))
      }
      ctx.fillStyle = l.c || '#000000'
      lines.forEach((s, i) => ctx.fillText(s, dx, dy - hAll + lead * (i + 0.8)))
      ctx.restore()
    }
  }

  /**
   * Podswietlenie rysujemy jako poswiate obrysowujaca faktyczna geometrie,
   * a symbolika obiektu wraca potem na wierzch, zeby podswietlenie ja
   * otaczalo, a nie zakrywalo.
   *
   * strong=false to zapowiedz z listy trafien: sam kontur, bez wypelnienia -
   * inaczej dwa rozowe poligony jeden na drugim nie roznilyby sie niczym.
   */
  private drawHighlight(id: number, strong: boolean, ctx: CanvasRenderingContext2D,
                        sx: (x: number) => number, sy: (y: number) => number, k: number) {
    const f = this.feature(id)
    if (!f) return

    const halo = strong ? SEL_HALO : PRE_HALO
    const fill = strong ? SEL_FILL : null
    const pad = strong ? SEL_PAD_PX : PRE_PAD_PX

    ctx.save()
    ctx.lineCap = 'round'
    ctx.lineJoin = 'round'
    ctx.setLineDash([])
    for (const o of f.ops) {
      if (o.op === 'fill') {
        ctx.beginPath()
        for (const poly of o.g) {
          for (const ring of poly) {
            ring.forEach((p, i) => i ? ctx.lineTo(sx(p[0]), sy(p[1])) : ctx.moveTo(sx(p[0]), sy(p[1])))
            ctx.closePath()
          }
        }
        if (fill) { ctx.fillStyle = fill; ctx.fill('evenodd') }
        ctx.strokeStyle = halo
        ctx.lineWidth = Math.max(o.w * k, 1) + pad
        ctx.stroke()
      } else if (o.op === 'line') {
        // Obrys powierzchni podswietlamy jak powierzchnie - z przezroczystym
        // wypelnieniem; prawdziwa polilinia dostaje sama poswiate.
        const area = isAreaGeom(o.g)
        ctx.beginPath()
        for (const ln of asLines(o.g)) {
          ln.forEach((p, i) => i ? ctx.lineTo(sx(p[0]), sy(p[1])) : ctx.moveTo(sx(p[0]), sy(p[1])))
          if (area) ctx.closePath()
        }
        if (area && fill) { ctx.fillStyle = fill; ctx.fill('evenodd') }
        ctx.strokeStyle = halo
        ctx.lineWidth = Math.max(o.w * k, 1) + pad
        ctx.stroke()
      } else {
        // punkty: krazek o promieniu symbolu powiekszonym o margines, zeby
        // najmniejsze znaki tez bylo widac
        const r = Math.max(o.s * k, MARKER_MIN_PX) / 2 + pad
        ctx.fillStyle = halo
        for (const p of o.g) {
          ctx.beginPath()
          ctx.arc(sx(p[0]), sy(p[1]), r, 0, Math.PI * 2)
          ctx.fill()
        }
      }
    }
    ctx.restore()

    for (const o of f.ops) this.drawOp(ctx, o, sx, sy, k)
  }

  private feature(id: number): RenderFeature | null {
    for (const L of this.data.layers) {
      for (const f of L.features) if (f.id === id) return f
    }
    return null
  }

  /**
   * WSZYSTKIE obiekty trafione klknieciem, uporzadkowane od wierzchu w dol -
   * liczone na prawdziwej geometrii, nie na prostokacie zasiegu.
   *
   * Zwracamy caly stos, bo w mapie zasadniczej obiekty rutynowo leza jeden na
   * drugim (uzytek na dzialce, dzialka pod budynkiem) i przy jednym trafieniu
   * do tych nizszych nie ma jak dojsc. Wyborem zajmuje sie panel; tutaj
   * ustalamy tylko kolejnosc.
   *
   * Wyzej stoi nizszy wymiar: punkt przed linia, linia przed powierzchnia.
   * Klikajac w symbol lezacy na dzialce chce sie zwykle tego symbolu, a nie
   * dzialki pod nim - a przy porownywaniu samych zasiegow wygrywal ten, kto
   * mial mniejszy prostokat, wiec waski budynek potrafil przeslonic punkt.
   */
  pickAll(x: number, y: number, tol: number): PickHit[] {
    const hits: PickHit[] = []

    for (const L of this.data.layers) {
      if (!this.visible_.has(L.name)) continue
      for (const f of L.features) {
        const b = f.bb
        if (!b) continue
        if (x < b[0] - tol || x > b[2] + tol || y < b[1] - tol || y > b[3] + tol) continue

        const hit = hitTest(f.ops, x, y, tol, u => this.aspect(u))
        if (!hit) continue
        // Powierzchnie rozstrzygamy wielkoscia (mniejsza wyzej, bo lezy "na
        // wierzchu"), punkty i linie - odlegloscia od kliknietego miejsca.
        const metric = hit.dim === 2 ? (b[2] - b[0]) * (b[3] - b[1]) : hit.d
        hits.push({ layer: L.name, id: f.id, dim: hit.dim, metric })
      }
    }

    hits.sort((a, b) => a.dim - b.dim || a.metric - b.metric)
    return hits
  }
}

// ------------------------------------------------------------------ pomoc

/**
 * Czy instrukcje rysujace obiekt siegaja punktu (x, y)?
 *
 * Zwraca wymiar trafionej geometrii (0 punkt, 1 linia, 2 powierzchnia) oraz
 * odleglosc - reszte rozstrzyga pick(). Tolerancja jest w jednostkach mapy;
 * dla symboli i grubych linii bierzemy wieksza z dwoch: tolerancje klikniecia
 * i polowe wlasnego rozmiaru, zeby dalo sie trafic w to, co widac.
 */
function hitTest(ops: Op[], x: number, y: number, tol: number,
                 aspect: (u: string) => number): { dim: number; d: number } | null {
  let point = Infinity
  let line = Infinity
  let inside = false

  for (const o of ops) {
    if (o.op === 'marker' || o.op === 'svg') {
      // Symbol SVG bywa wyzszy niz szerszy (hydrant, latarnia), wiec trafienie
      // liczymy w jego wlasnym prostokacie - kolo o promieniu polowy szerokosci
      // nie obejmowalo czubka symbolu, w ktory widac, ze sie klika.
      const rx = Math.max(tol, o.s / 2)
      const ry = o.op === 'svg' ? Math.max(tol, o.s * aspect(o.u) / 2) : rx
      for (const p of o.g) {
        const dx = p[0] - x
        const dy = p[1] - y
        if (Math.abs(dx) > rx || Math.abs(dy) > ry) continue
        const d = Math.hypot(dx, dy)
        if (d < point) point = d
      }
      continue
    }

    if (o.op === 'line') {
      const r = Math.max(tol, o.w / 2)
      // Mapa zasadnicza rysuje dzialki i budynki samym obrysem, wiec ich
      // geometria przychodzi jako 'line' - ale nadal jest powierzchnia i ma
      // sie identyfikowac klikiem w srodek, tak jak w QGIS-ie (ktory pyta
      // geometrii, a nie tego, czy cos zostalo wypelnione).
      if (isAreaGeom(o.g)) {
        if (!inside) inside = inArea(o.g as unknown as number[][][][], x, y, r)
      } else {
        for (const ln of o.g) {
          const d = distToPath(ln, x, y)
          if (d <= r && d < line) line = d
        }
      }
      continue
    }

    if (!inside) inside = inArea(o.g, x, y, tol)
  }

  if (point < Infinity) return { dim: 0, d: point }
  if (line < Infinity) return { dim: 1, d: line }
  return inside ? { dim: 2, d: 0 } : null
}

/** Wnetrze powierzchni albo pas wzdluz jej granicy. */
function inArea(polys: number[][][][], x: number, y: number, tol: number): boolean {
  for (const poly of polys) {
    if (pointInRings(poly, x, y)) return true
    for (const ring of poly) if (distToPath(ring, x, y) <= tol) return true
  }
  return false
}

/** Najmniejsza odleglosc punktu od lamanej (dla pojedynczego wierzcholka - od niego). */
function distToPath(pts: number[][], x: number, y: number): number {
  if (!pts.length) return Infinity
  if (pts.length === 1) return Math.hypot(pts[0][0] - x, pts[0][1] - y)
  let best = Infinity
  for (let i = 1; i < pts.length; i++) {
    const d = distToSeg(x, y, pts[i - 1][0], pts[i - 1][1], pts[i][0], pts[i][1])
    if (d < best) best = d
  }
  return best
}

function distToSeg(px: number, py: number,
                   x1: number, y1: number, x2: number, y2: number): number {
  const dx = x2 - x1, dy = y2 - y1
  const len2 = dx * dx + dy * dy
  const t = len2 ? Math.max(0, Math.min(1, ((px - x1) * dx + (py - y1) * dy) / len2)) : 0
  return Math.hypot(px - (x1 + t * dx), py - (y1 + t * dy))
}

/** Zasada parzystosci przeciec - liczona po wszystkich pierscieniach naraz, wiec dziury wypadaja same. */
function pointInRings(rings: number[][][], x: number, y: number): boolean {
  let odd = false
  for (const ring of rings) {
    for (let i = 0, j = ring.length - 1; i < ring.length; j = i++) {
      const [xi, yi] = ring[i], [xj, yj] = ring[j]
      if ((yi > y) !== (yj > y) && x < (xj - xi) * (y - yi) / (yj - yi) + xi) odd = !odd
    }
  }
  return odd
}

/**
 * Obrys poligonu przychodzi o poziom glebiej niz polilinia (geom_coords w
 * kompilatorze zagniezdza wedlug typu geometrii, nie wedlug symbolu), wiec po
 * samej glebokosci listy poznajemy, czy 'line' opisuje powierzchnie.
 */
function isAreaGeom(g: any): boolean {
  return !!(g.length && Array.isArray(g[0]) && Array.isArray(g[0][0]) && Array.isArray(g[0][0][0]))
}

/** Obrys poligonu -> lista pierscieni, polilinia zostaje jak byla. */
function asLines(g: any): number[][][] {
  return isAreaGeom(g) ? g.flat() : g
}

/**
 * Ksztalty markerow QGIS. Odtworzone z faktycznego renderu (app.diag_shapes),
 * nie z pamieci - 'half_square' to LEWA polowa kwadratu, a 'arrowhead' to
 * otwarte ">" wskazujace na wschod, a nie wypelniony trojkat.
 *
 * Uklad lokalny ma os Y w dol, tak jak QPainter w QGIS.
 */
const STROKE_ONLY = new Set(['line', 'cross', 'cross2', 'arrowhead', 'half_arc'])

function markerPath(ctx: CanvasRenderingContext2D, name: string, s: number) {
  const h = s / 2
  ctx.beginPath()
  switch (name) {
    case 'circle':
      ctx.arc(0, 0, h, 0, Math.PI * 2); break
    case 'line':
      ctx.moveTo(0, -h); ctx.lineTo(0, h); break
    case 'cross':
    case 'cross2':
      ctx.moveTo(-h, 0); ctx.lineTo(h, 0); ctx.moveTo(0, -h); ctx.lineTo(0, h); break
    case 'cross_fill':
      ctx.moveTo(-h, -h / 3); ctx.lineTo(-h / 3, -h / 3); ctx.lineTo(-h / 3, -h)
      ctx.lineTo(h / 3, -h); ctx.lineTo(h / 3, -h / 3); ctx.lineTo(h, -h / 3)
      ctx.lineTo(h, h / 3); ctx.lineTo(h / 3, h / 3); ctx.lineTo(h / 3, h)
      ctx.lineTo(-h / 3, h); ctx.lineTo(-h / 3, h / 3); ctx.lineTo(-h, h / 3)
      ctx.closePath(); break
    case 'arrowhead':                       // otwarte ">" - tylko obrys
      ctx.moveTo(-h, -h); ctx.lineTo(0, 0); ctx.lineTo(-h, h); break
    case 'half_square':                     // lewa polowa kwadratu
      ctx.rect(-h, -h, h, s); break
    case 'diamond':
      ctx.moveTo(0, -h); ctx.lineTo(h, 0); ctx.lineTo(0, h); ctx.lineTo(-h, 0)
      ctx.closePath(); break
    case 'triangle':
      ctx.moveTo(0, -h); ctx.lineTo(h, h); ctx.lineTo(-h, h); ctx.closePath(); break
    default:                                // 'square' i nieznane
      ctx.rect(-h, -h, s, s)
  }
}

function bboxOf(ops: Op[]): [number, number, number, number] {
  let x0 = Infinity, y0 = Infinity, x1 = -Infinity, y1 = -Infinity
  const walk = (g: any) => {
    if (!Array.isArray(g) || !g.length) return
    if (typeof g[0] === 'number') {
      if (g[0] < x0) x0 = g[0]; if (g[0] > x1) x1 = g[0]
      if (g[1] < y0) y0 = g[1]; if (g[1] > y1) y1 = g[1]
      return
    }
    for (const p of g) walk(p)
  }
  for (const o of ops) walk((o as any).g)
  return [x0, y0, x1, y1]
}
